import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { Stars, Verified } from '@mui/icons-material';
import USER_BADGE_TYPES from 'config/UserBadgeTypes';
import clsx from 'clsx';
import useStyles from './styles';
import { BadgeType } from '@elevator/ui';

/**
 * UserBadge Component
 *
 * Displays a badge icon indicating user status (Staff, Preferred, or Verified).
 * Each badge variant has a distinct icon and background color, and displays
 * a tooltip on hover showing the badge type.
 *
 * @param {UserBadgePropTypes} props - Props for the component.
 * @param {BadgeType} props.variant - The type of badge to display ('staff', 'preferred', or 'verified').
 */
const UserBadge = ({ variant }: UserBadgePropTypes) => {
  const { classes } = useStyles();

  const [arrowRef, setArrowRef] = useState<HTMLElement | null>(null);

  const renderBadgeVariant = () => {
    switch (variant) {
      case USER_BADGE_TYPES.STAFF:
        return (
          <svg
            className={classes.staffIcon}
            viewBox="0 0 151 151"
            width={16}
            height={16}
            aria-label="Elevator Staff Badge"
          >
            <path
              d="M79.5297572,24 L88.6068756,37.0058727 L116.794399,37.0595795 L144,99.0168152 L124.872494,128 L70.524422,128 L63.3839387,114.993496 L34.1942726,114.993496 L7,53.0039703 L26.1558706,24 L79.5297572,24 Z M98.9323019,46.9994295 L71.1409942,109.986851 L126.72361,109.986851 L98.9323019,46.9994295 Z M82.1585,45.9996292 L25.6936208,45.9996292 L53.9260604,109.986851 L82.1585,45.9996292 Z"
              fill="currentColor"
            />
          </svg>
        );
      case USER_BADGE_TYPES.PREFERRED:
        return <Stars className={classes.preferredIcon} />;
      case USER_BADGE_TYPES.VERIFIED:
        return <Verified className={classes.verifiedBadge} />;

      default:
        return null;
    }
  };

  return (
    <Box className={classes.root} data-component="UserBadge">
      <Tooltip
        title={
          <>
            {`ELEVATOR ${variant.toUpperCase()} MEMBER`}
            <span className={classes.arrow} ref={setArrowRef} />
          </>
        }
        classes={{
          tooltip: classes.tooltip,
          popper: classes.popper,
          tooltipPlacementBottom: classes.tooltipPlacementBottom,
        }}
        disableFocusListener
        disableTouchListener
        slotProps={{
          popper: {
            popperOptions: {
              modifiers: [
                {
                  name: 'arrow',
                  enabled: Boolean(arrowRef),
                  options: {
                    element: arrowRef,
                  },
                },
              ],
            },
          },
        }}
        placement="bottom"
      >
        <Box
          className={clsx(classes.badge, {
            [classes.staffBackground]: variant === USER_BADGE_TYPES.STAFF,
          })}
        >
          {renderBadgeVariant()}
        </Box>
      </Tooltip>
    </Box>
  );
};

export type UserBadgePropTypes = {
  /** The type of badge to display ('staff', 'preferred', or 'verified'). */
  variant: BadgeType;
};

export default UserBadge;
